import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Bell,
  Building2,
  UserPlus,
  UserMinus,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  ChevronRight,
  RefreshCw,
  Eye
} from 'lucide-react';

export type NotificationRole = 'super_admin' | 'agency_admin' | 'supervisor' | 'patient';

interface ActivityItem {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  severity?: 'info' | 'success' | 'warning' | 'error';
  link?: string;
  metadata?: Record<string, any>;
}

interface NotificationCenterProps {
  role: NotificationRole;
  agencyId?: string;
  patientId?: string;
  className?: string;
  maxItems?: number;
}

const activityIcons: Record<string, React.ElementType> = {
  agency_created: Building2,
  agency_approved: CheckCircle,
  agency_suspended: AlertTriangle,
  agency_deleted: AlertTriangle,
  supervisor_added: UserPlus,
  supervisor_removed: UserMinus,
  patient_registered: UserPlus,
  patient_assigned: UserPlus,
  patient_unassigned: UserMinus,
  triage_completed: FileText,
  red_flag_alert: AlertTriangle,
  session_started: Clock,
  default: Activity
};

const severityColors: Record<string, string> = {
  info: 'bg-blue-500',
  success: 'bg-green-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500'
};

export default function NotificationCenter({
  role,
  agencyId,
  patientId,
  className,
  maxItems = 10
}: NotificationCenterProps) {
  const { user: adminUser } = useAdminAuth();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchActivities = async () => {
    try {
      let items: ActivityItem[] = [];

      switch (role) {
        case 'super_admin':
          // Fetch agency audit logs for super admin
          const { data: auditLogs } = await supabase
            .from('agency_audit_logs')
            .select('id, action, agency_name, actor_email, created_at, metadata')
            .order('created_at', { ascending: false })
            .limit(maxItems);

          if (auditLogs) {
            items = auditLogs.map(log => ({
              id: log.id,
              type: log.action,
              title: formatActionTitle(log.action),
              description: `${log.agency_name}${log.actor_email ? ` by ${log.actor_email}` : ''}`,
              timestamp: log.created_at,
              severity: getActionSeverity(log.action),
              link: '/admin/agencies'
            }));
          }
          break;

        case 'agency_admin':
          // Fetch recent triage sessions and patient activities for agency
          if (agencyId) {
            const [sessionsResult, patientsResult] = await Promise.all([
              supabase
                .from('triage_sessions')
                .select('id, patient_id, recommendation, completed_at, red_flags, patients(first_name, last_name)')
                .eq('agency_id', agencyId)
                .not('completed_at', 'is', null)
                .order('completed_at', { ascending: false })
                .limit(maxItems / 2),
              supabase
                .from('patients')
                .select('id, first_name, last_name, created_at')
                .eq('agency_id', agencyId)
                .order('created_at', { ascending: false })
                .limit(maxItems / 2)
            ]);

            if (sessionsResult.data) {
              items.push(...sessionsResult.data.map(session => {
                const patient = session.patients as any;
                return {
                  id: session.id,
                  type: session.red_flags ? 'red_flag_alert' : 'triage_completed',
                  title: session.red_flags ? 'Red Flag Alert' : 'Assessment Completed',
                  description: `${patient?.first_name || ''} ${patient?.last_name || ''} - ${session.recommendation || 'Pending'}`,
                  timestamp: session.completed_at!,
                  severity: session.red_flags ? 'error' : getRecommendationSeverity(session.recommendation),
                  link: `/admin/patients/${session.patient_id}`
                };
              }));
            }

            if (patientsResult.data) {
              items.push(...patientsResult.data.map(patient => ({
                id: patient.id,
                type: 'patient_registered',
                title: 'New Patient Registered',
                description: `${patient.first_name} ${patient.last_name}`,
                timestamp: patient.created_at,
                severity: 'info' as const,
                link: `/admin/patients/${patient.id}`
              })));
            }

            // Sort by timestamp
            items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            items = items.slice(0, maxItems);
          }
          break;

        case 'supervisor':
          // Fetch supervisor notifications
          if (adminUser?.id) {
            const { data: notifications } = await supabase
              .from('supervisor_notifications')
              .select('id, notification_type, title, message, created_at, patient_id, is_read')
              .eq('supervisor_user_id', adminUser.id)
              .order('created_at', { ascending: false })
              .limit(maxItems);

            if (notifications) {
              items = notifications.map(n => ({
                id: n.id,
                type: n.notification_type,
                title: n.title,
                description: n.message,
                timestamp: n.created_at,
                severity: n.notification_type === 'red_flag_alert' ? 'error' : 'info',
                link: n.patient_id ? `/admin/patients/${n.patient_id}` : undefined,
                metadata: { is_read: n.is_read }
              }));
            }
          }
          break;

        case 'patient':
          // Fetch patient's triage sessions
          if (authUser?.id) {
            const { data: patientData } = await supabase
              .from('patients')
              .select('id')
              .eq('user_id', authUser.id)
              .single();

            if (patientData) {
              const { data: sessions } = await supabase
                .from('triage_sessions')
                .select('id, recommendation, completed_at, created_at, red_flags')
                .eq('patient_id', patientData.id)
                .order('created_at', { ascending: false })
                .limit(maxItems);

              if (sessions) {
                items = sessions.map(s => ({
                  id: s.id,
                  type: s.completed_at ? 'triage_completed' : 'session_started',
                  title: s.completed_at ? 'Assessment Completed' : 'Assessment In Progress',
                  description: s.completed_at 
                    ? `Recommendation: ${formatRecommendation(s.recommendation)}`
                    : 'Continue your assessment',
                  timestamp: s.completed_at || s.created_at,
                  severity: s.red_flags ? 'error' : getRecommendationSeverity(s.recommendation),
                  link: s.completed_at ? `/results/${s.id}` : '/triage'
                }));
              }
            }
          }
          break;
      }

      setActivities(items);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchActivities();

    // Set up realtime subscriptions based on role
    let channel: ReturnType<typeof supabase.channel> | null = null;

    if (role === 'super_admin') {
      channel = supabase
        .channel('notification-center-super-admin')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'agency_audit_logs' }, fetchActivities)
        .subscribe();
    } else if (role === 'agency_admin' && agencyId) {
      channel = supabase
        .channel('notification-center-agency')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'triage_sessions', filter: `agency_id=eq.${agencyId}` }, fetchActivities)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'patients', filter: `agency_id=eq.${agencyId}` }, fetchActivities)
        .subscribe();
    } else if (role === 'supervisor' && adminUser?.id) {
      channel = supabase
        .channel('notification-center-supervisor')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'supervisor_notifications', filter: `supervisor_user_id=eq.${adminUser.id}` }, fetchActivities)
        .subscribe();
    }

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [role, agencyId, adminUser?.id, authUser?.id]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchActivities();
  };

  const handleItemClick = (activity: ActivityItem) => {
    if (activity.link) {
      navigate(activity.link);
    }
  };

  const getIcon = (type: string) => {
    return activityIcons[type] || activityIcons.default;
  };

  if (loading) {
    return (
      <Card className={cn('bg-white border-gray-200', className)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Bell className="h-5 w-5 text-[#1F4E79]" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('bg-white border-gray-200', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg text-gray-900">
            <Bell className="h-5 w-5 text-[#1F4E79]" />
            Recent Activity
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-8 w-8 p-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Bell className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-600">No recent activity</p>
            <p className="text-xs text-gray-500 mt-1">
              Activity will appear here as it happens
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[320px] pr-4">
            <div className="space-y-3">
              {activities.map((activity) => {
                const Icon = getIcon(activity.type);
                const isUnread = activity.metadata?.is_read === false;

                return (
                  <div
                    key={activity.id}
                    onClick={() => handleItemClick(activity)}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer",
                      "hover:bg-gray-100",
                      isUnread && "bg-[#1F4E79]/5",
                      activity.link && "cursor-pointer"
                    )}
                  >
                    <div className={cn(
                      "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                      activity.severity === 'error' && "bg-red-100 text-red-600",
                      activity.severity === 'warning' && "bg-[#B9770E]/10 text-[#B9770E]",
                      activity.severity === 'success' && "bg-[#1E8449]/10 text-[#1E8449]",
                      activity.severity === 'info' && "bg-[#1F4E79]/10 text-[#1F4E79]",
                      !activity.severity && "bg-gray-100 text-gray-600"
                    )}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {activity.title}
                        </p>
                        {isUnread && (
                          <span className="h-2 w-2 rounded-full bg-[#1F4E79] flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 truncate mt-0.5">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                    {activity.link && (
                      <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

// Helper functions
function formatActionTitle(action: string): string {
  const titles: Record<string, string> = {
    agency_created: 'New Agency Registered',
    agency_approved: 'Agency Approved',
    agency_suspended: 'Agency Suspended',
    agency_deleted: 'Agency Deleted',
    supervisor_added: 'Supervisor Added',
    supervisor_removed: 'Supervisor Removed',
    patient_registered: 'Patient Registered'
  };
  return titles[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getActionSeverity(action: string): 'info' | 'success' | 'warning' | 'error' {
  if (action.includes('approved')) return 'success';
  if (action.includes('suspended') || action.includes('deleted')) return 'warning';
  if (action.includes('added') || action.includes('created') || action.includes('registered')) return 'info';
  return 'info';
}

function getRecommendationSeverity(rec: string | null): 'info' | 'success' | 'warning' | 'error' {
  switch (rec) {
    case 'ER_NOW': return 'error';
    case 'URGENT_CARE': return 'warning';
    case 'PCP': return 'info';
    case 'HOME': return 'success';
    default: return 'info';
  }
}

function formatRecommendation(rec: string | null): string {
  switch (rec) {
    case 'HOME': return 'Stay Home';
    case 'PCP': return 'See Doctor';
    case 'URGENT_CARE': return 'Urgent Care';
    case 'ER_NOW': return 'Emergency';
    default: return 'Pending';
  }
}
